import { can, formatDate } from '@clim/client-core';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { errorMessage } from '../lib/errors';
import { ErrorBox, Field, Input, Modal } from './ui';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useEngine, useReady, useSession } from '../lib/session';
import { TerritoryProvider, useTerritory } from '../lib/territory';
import { useOnline } from '../lib/use-online';

/**
 * Session expirée : on redemande le mot de passe du même compte, SANS se déconnecter. Les modifications et fichiers
 * en attente restent sur l'appareil et partent dès la reconnexion.
 */
function ReloginDialog({ onClose }: { onClose(): void }) {
  const { me } = useReady();
  const { relogin, reloginMfa } = useSession();
  const engine = useEngine();
  const [password, setPassword] = useState('');
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mfaToken) await reloginMfa(mfaToken, code);
      else {
        const mfa = await relogin(password);
        if (mfa) return setMfaToken(mfa.mfaToken);
      }
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const pending = engine.pendingCount + engine.pendingUploads;
  return (
    <Modal title="Reconnexion" onClose={onClose}>
      <form className="form" onSubmit={(e) => void submit(e)}>
        <p>
          Votre session a expiré. Saisissez à nouveau votre mot de passe pour continuer :{' '}
          <strong>rien n’est perdu</strong>
          {pending > 0 && ` (${engine.pendingCount} modification(s) et ${engine.pendingUploads} fichier(s) en attente seront envoyés)`}.
        </p>
        <p className="muted">{me.user.email}</p>
        {mfaToken ? (
          <Field label="Code de vérification">
            <Input autoFocus inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} />
          </Field>
        ) : (
          <Field label="Mot de passe">
            <Input autoFocus type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
          </Field>
        )}
        <ErrorBox message={error} />
        <div className="actions">
          <button type="button" className="btn" onClick={onClose}>
            Plus tard
          </button>
          <button className="btn primary" disabled={busy}>
            Se reconnecter
          </button>
        </div>
      </form>
    </Modal>
  );
}

function SyncBadge() {
  const engine = useEngine();
  const online = useOnline();
  const navigate = useNavigate();
  const state = !online || engine.status === 'offline' ? 'offline' : engine.status;
  // la fenêtre s'ouvre d'elle-même quand la session expire ; « Plus tard » la ferme, le badge la rouvre
  const [relogin, setRelogin] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    if (state !== 'auth_required') setDismissed(false);
  }, [state]);
  const onRelogin = () => setRelogin(true);
  const showRelogin = relogin || (state === 'auth_required' && !dismissed);
  const label = {
    offline: 'Hors ligne',
    syncing: 'Synchronisation…',
    auth_required: 'Reconnexion requise',
    error: 'Erreur de synchro',
    idle: 'À jour',
  }[state];
  return (
    <div className="sync">
      <button
        className={`sync-badge sync-${state}`}
        onClick={() => (state === 'auth_required' ? onRelogin() : void engine.sync())}
        title={
          engine.lastSyncAt ? `Dernière synchro : ${formatDate(engine.lastSyncAt)}` : 'Jamais synchronisé'
        }
      >
        <span className="sync-dot" />
        {label}
        {engine.pendingCount > 0 && <span className="sync-count">{engine.pendingCount} à synchroniser</span>}
        {engine.pendingUploads > 0 && (
          <span className="sync-count">{engine.pendingUploads} fichier(s) à envoyer</span>
        )}
      </button>
      {engine.issues.length > 0 && (
        <button
          className="sync-issues"
          onClick={() => navigate('/sync')}
          aria-label={`${engine.issues.length} alerte(s) de synchronisation`}
        >
          ⚠ {engine.issues.length}
        </button>
      )}
      {showRelogin && state === 'auth_required' && (
        <ReloginDialog onClose={() => (setRelogin(false), setDismissed(true))} />
      )}
    </div>
  );
}

function Bell() {
  const engine = useEngine();
  const navigate = useNavigate();
  const n = engine.unreadNotifications;
  return (
    <button
      className="bell"
      onClick={() => navigate('/notifications')}
      aria-label={n ? `${n} notification(s) non lue(s)` : 'Notifications'}
    >
      🔔{n > 0 && <span className="bell-count">{n}</span>}
    </button>
  );
}

function TerritorySelect() {
  const { territoryId, setTerritoryId, choices } = useTerritory();
  if (choices.length < 2) return null;
  return (
    <select
      className="territory-select"
      value={territoryId ?? ''}
      onChange={(e) => setTerritoryId(e.target.value || null)}
      aria-label="Territoire"
    >
      <option value="">Tous les territoires</option>
      {choices.map((t) => (
        <option key={t.id} value={t.id}>
          {t.name}
        </option>
      ))}
    </select>
  );
}

export function isAdminArea(me: Parameters<typeof can>[0]) {
  return (
    can(me, 'catalog.manage') ||
    can(me, 'user.read') ||
    can(me, 'role.read') ||
    can(me, 'territory.manage') ||
    can(me, 'referential.manage') ||
    can(me, 'audit.read') ||
    can(me, 'tenant.settings.manage')
  );
}

/**
 * §15 : pendant que l'application Web est ouverte, une notification « push » (événement important, annonce urgente)
 * s'affiche aussi comme notification du système si l'utilisateur l'a autorisé. Sur mobile : push natif.
 */
function useSystemNotifications() {
  const engine = useEngine();
  const navigate = useNavigate();
  const seen = useRef<Set<string> | null>(null);
  useEffect(() => {
    const list = engine.list('notification');
    if (!seen.current) {
      seen.current = new Set(list.map((n) => n.id));
      return;
    }
    for (const n of list) {
      if (seen.current.has(n.id)) continue;
      seen.current.add(n.id);
      if (!n.push || n.readAt || typeof Notification === 'undefined' || Notification.permission !== 'granted')
        continue;
      try {
        const x = new Notification(n.title, { body: n.body ?? undefined, tag: n.id });
        x.onclick = () => {
          window.focus();
          void engine.markNotificationRead(n.id);
          navigate(n.link ?? (n.dossierId ? `/dossiers/${n.dossierId}` : '/notifications'));
        };
      } catch {
        // some mobile browsers only allow notifications from a service worker
      }
    }
  }, [engine, engine.version, navigate]);
}

function Shell() {
  const { me } = useReady();
  useSystemNotifications();
  const { logout } = useSession();
  const engine = useEngine();
  const onLogout = async () => {
    const lost = engine.pendingCount + engine.pendingUploads;
    if (
      lost > 0 &&
      !confirm(
        `${engine.pendingCount} modification(s) et ${engine.pendingUploads} fichier(s) non envoyés seront perdus. Se déconnecter ?`,
      )
    )
      return;
    await logout();
  };
  return (
    <div className="app">
      <header className="topbar">
        <NavLink to="/" className="brand" aria-label="Accueil">
          {me.tenant.settings.branding.logoUrl ? (
            <img src={me.tenant.settings.branding.logoUrl} alt={me.tenant.name} className="brand-logo" />
          ) : (
            <>
              <img src="/icon.svg" alt="" width={28} height={28} />
              <span>{me.tenant.name}</span>
            </>
          )}
        </NavLink>
        <SyncBadge />
        <Bell />
        <TerritorySelect />
        <div className="user">
          <NavLink to="/securite" className="user-name" title="Sécurité du compte">
            {me.user.firstName}
          </NavLink>
          <button className="btn ghost small" onClick={() => void onLogout()}>
            Déconnexion
          </button>
        </div>
      </header>
      {/* Navigation limitée à 4-5 entrées (CDC §3) ; le reste passe par le hub. */}
      <nav className="tabs">
        <NavLink to="/" end>
          Accueil
        </NavLink>
        <NavLink to="/dossiers">Dossiers</NavLink>
        {can(me, 'client.read') && <NavLink to="/clients">Clients</NavLink>}
        {can(me, 'planning.read') && <NavLink to="/planning">Planning</NavLink>}
        {isAdminArea(me) && <NavLink to="/admin">Administration</NavLink>}
      </nav>
      <main className="content">
        <Outlet />
      </main>
    </div>
  );
}

export function Layout() {
  return (
    <TerritoryProvider>
      <Shell />
    </TerritoryProvider>
  );
}
